'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Menu, X } from 'lucide-react';
import BrandLogo from '@/components/brand/BrandLogo';
import ThemeToggle from '@/components/ui/ThemeToggle';

interface StickyNavigationProps {
    isOpen?: boolean;
    isAuthenticated?: boolean;
}

const publicLinks = [
    { label: 'Free Tools', href: '/#free-tools' },
    { label: 'Astrology', href: '/astrology' },
    { label: 'Numerology', href: '/numerology' },
    { label: 'Blog', href: '/blog' },
];

const StickyNavigation = ({ isOpen = false, isAuthenticated = false }: StickyNavigationProps) => {
    const [menuOpen, setMenuOpen] = useState(isOpen);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (!menuOpen) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setMenuOpen(false);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [menuOpen]);

    const todayHref = isAuthenticated ? '/dashboard' : '/personalized-daily-horoscope';

    return (
        <nav
            aria-label="Primary navigation"
            className={`fixed inset-x-0 top-0 z-[1000] border-b transition-colors ${scrolled || menuOpen
                ? 'border-[var(--experience-border)] bg-[var(--experience-page)]'
                : 'border-transparent bg-[var(--experience-page)]'
                }`}
        >
            <div className="container mx-auto flex h-20 w-full min-w-0 items-center justify-between px-5">
                <Link href="/" aria-label="YourAura home" className="min-w-0 text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                    <BrandLogo size="lg" priority />
                </Link>

                <div className="hidden items-center gap-1 lg:flex">
                    {publicLinks.map((item) => (
                        <Link key={item.href} href={item.href} className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-[var(--experience-surface-quiet)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                            {item.label}
                        </Link>
                    ))}
                    <Link href={todayHref} className="manuscript-button rounded-lg px-4 py-2 text-sm font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                        Today
                    </Link>
                    <div className="ml-2"><ThemeToggle /></div>
                    {isAuthenticated ? (
                        <Link href="/dashboard" className="ml-2 rounded-lg border border-[var(--experience-border)] px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-accent/40 hover:bg-[var(--experience-surface-quiet)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">Dashboard</Link>
                    ) : (
                        <Link href="/login" className="ml-2 rounded-lg border border-[#e8d7b0]/16 px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-accent/40 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                            Sign In
                        </Link>
                    )}
                </div>

                <button
                    type="button"
                    onClick={() => setMenuOpen((open) => !open)}
                    className="fixed right-5 top-5 z-[1001] grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-[#e8d7b0]/14 bg-[var(--experience-page)] text-foreground transition-colors hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent lg:hidden"
                    aria-expanded={menuOpen}
                    aria-controls="mobile-navigation"
                    aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
                >
                    {menuOpen ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {menuOpen && (
                <div id="mobile-navigation" className="border-t border-[var(--experience-border)] bg-[var(--experience-page)] px-5 pb-6 pt-3 lg:hidden">
                    <div className="container mx-auto grid gap-1">
                        {publicLinks.map((item) => (
                            <Link key={`${item.label}-${item.href}`} href={item.href} onClick={() => setMenuOpen(false)} className="rounded-lg px-4 py-3 text-base font-medium text-muted-foreground hover:bg-[var(--experience-surface-quiet)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                                {item.label}
                            </Link>
                        ))}
                        <Link href={todayHref} onClick={() => setMenuOpen(false)} className="manuscript-button mt-2 rounded-lg px-4 py-3 text-center text-base font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                            Today
                        </Link>
                        <div className="flex items-center justify-between rounded-lg px-4 py-3 text-sm font-medium text-[var(--experience-text-soft)]">
                            Theme <ThemeToggle />
                        </div>
                        <Link href={isAuthenticated ? '/dashboard' : '/login'} onClick={() => setMenuOpen(false)} className="mt-2 rounded-lg border border-[var(--experience-border)] px-4 py-3 text-center text-sm font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                            {isAuthenticated ? 'Open Dashboard' : 'Sign In'}
                        </Link>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default StickyNavigation;
